// externals
import type { Reducer } from 'redux';
import type { TradeInSlideoutTab } from '../types/types';

// consts/enums
import { DealTabs } from '@makemydeal/dr-shared-types';
import {
    SET_BREAKDOWN,
    SET_ACTIVE_BREAKDOWN_TAB_DETAILS,
    TAB_CHANGE,
    SAVE_VEHICLE_INSURANCE_INFO,
    SHOW_VEHICLE_INSURANCE,
    SHOW_CREDIT_BUREAU,
    SHOW_BREAKDOWN_SLIDEOUT,
    SHOW_CUSTOMER_INSIGHTS_SLIDEOUT,
    SHOW_REPAIR_ORDERS_SLIDEOUT,
    INCENTIVES_SLIDEOUT_TAB_CHANGE,
    SET_FUNCTIONS_MODAL,
    SET_AUDIT_LOG_SLIDEOUT,
    COMPLIANCE_SLIDEOUT_TAB_CHANGE,
    TRADE_IN_SLIDEOUT_TAB_CHANGE,
    SET_IS_PAYMENT_CALL_PENDING,
    SET_PAYMENT_IN_FLIGHT_COUNT,
    SET_IS_AUTOSELECT_PROGRAM_CHECKED,
    SET_CALC_ENGINE_TRANSITION_STATE,
    CLEAR_AIO_DXG_EVENT_ERRORS,
    DISMISS_FS_SYNC_FAILURE,
    FS_SYNC_FINAL_ATTEMPT_TIMED_OUT,
    OPEN_SLIDEOUT,
    CLOSE_SLIDEOUT,
    SHOW_UPDATE_OFFER_FOR_FS_EVENTS,
    SET_PENDING_NAVIGATION,
    CLEAR_PENDING_NAVIGATION,
    UPDATE_RIGHT_TO_CANCEL_DAYS,
    UPDATE_RIGHT_TO_CANCEL_CHARGE
} from '../actionTypes/baseDealActionTypes';
import { AIO_DXG_EVENTS, SOCKET_EVENT_RECEIVED } from '../actionTypes/socketActionTypes';
import { BALLOON_FALLBACK_HIGHLIGHT, BALLOON_FALLBACK_HIGHLIGHT_CLEAR } from '../actions/balloonFallbackActionCreators';

// libraries
import { initActionTypes } from '@makemydeal/dr-shared-store';
import { SET_SLIDEOUT_CONTENT, SHOW_CUSTOMER_RISK_AND_COVERAGE_SLIDEOUT } from '../actionTypes/customerRiskAndCoverageActionTypes';
import { HIDE_FAILED_UPDATE_OFFER_MODAL, SHOW_FAILED_UPDATE_OFFER_MODAL } from '../types/orders';
import type { CalcEngineTransitionStateTypes } from '../types/calcEngineTransitionStateTypes';
import type { AioDxgEvent, AioFSFailure } from '../types/types';
import type { AioFsSyncFailure } from '../types/fsSyncFailure';

const { INIT_PENCIL_SUCCESS } = initActionTypes;

type BaseDealState = {
    activeTab: string;
    showBreakdown: boolean;
    activeBreakdownTabDetails?: Record<string, string>;
    vehicleInsuranceInfo: null;
    showVehicleInsurance: boolean;
    showVehicleCompliance: boolean;
    isVehicleInsuranceUpdated?: boolean;
    showBreakdownSlideout: boolean;
    showCustomerInsightsSlideout: boolean;
    showRepairOrdersSlideout: boolean;
    repairOrdersSlideoutVin?: string;
    incentivesSlideout: {
        activeTab: 'included' | 'manualUpdates';
    };
    customerRiskAndCoverageSlideout: {
        activeTab: 'insurance' | 'compliance';
    };
    tradeInSlideout: {
        activeTab: TradeInSlideoutTab;
    };
    showFunctionsModal: boolean;
    showAuditLogSlideout?: boolean;
    slideOutContent: null;
    breakdownActionSource?: 'click' | 'hotkey';
    paymentCallPending?: boolean;
    inFlightPaymentCount?: number;
    showFailedUpdateOfferModal?: boolean;
    isAutoSelectProgramChecked?: boolean;
    calcEngineTransitionState?: CalcEngineTransitionStateTypes;
    aioDxgEvents?: {
        fsFailures?: AioFSFailure[];
        fsSyncFailure?: AioFsSyncFailure;
    };
    currentSlideOut?: string;
    previousSlideOut?: string;
    appraisalTradeInIndex?: number;
    showUpdateOfferForFSEvents?: boolean;
    pendingNavigation?: string;
    hasBalloonFallbackOccurred?: boolean;
    rightToCancelDetails?: {
        rightToCancelDays?: number;
        rightToCancelCharge?: number;
    };
};

export const BASE_DEAL_INITIAL_STATE: BaseDealState = {
    activeTab: DealTabs.SUMMARY,
    showBreakdown: false,
    activeBreakdownTabDetails: {},
    vehicleInsuranceInfo: null,
    showVehicleInsurance: false,
    isVehicleInsuranceUpdated: false,
    showBreakdownSlideout: false,
    showCustomerInsightsSlideout: false,
    showRepairOrdersSlideout: false,
    incentivesSlideout: {
        activeTab: 'included'
    },
    customerRiskAndCoverageSlideout: {
        activeTab: 'insurance'
    },
    tradeInSlideout: {
        activeTab: 'trade-in'
    },
    showFunctionsModal: false,
    showVehicleCompliance: false,
    showAuditLogSlideout: false,
    slideOutContent: null,
    breakdownActionSource: undefined,
    paymentCallPending: false,
    inFlightPaymentCount: 0,
    isAutoSelectProgramChecked: undefined,
    calcEngineTransitionState: {
        fniMode: undefined,
        cashDeal: undefined,
        paymentGrid: undefined,
        flexBuy: undefined
    },
    currentSlideOut: 'none',
    showUpdateOfferForFSEvents: false,
    pendingNavigation: '',
    hasBalloonFallbackOccurred: false,
    rightToCancelDetails: {}
};

export type BaseDealReducer = Reducer<BaseDealState>;

type AioDxgEventsState = BaseDealState['aioDxgEvents'];

const withoutFsSyncFailure = (aioDxgEvents: AioDxgEventsState): AioDxgEventsState => {
    if (!aioDxgEvents || !('fsSyncFailure' in aioDxgEvents)) {
        return aioDxgEvents;
    }

    const { fsSyncFailure: _fsSyncFailure, ...remaining } = aioDxgEvents;

    return remaining;
};

export const reducer: Reducer<BaseDealState> = (state: BaseDealState = BASE_DEAL_INITIAL_STATE, action: any) => {
    switch (action.type) {
        case TAB_CHANGE:
            return {
                ...state,
                activeTab: action.payload.activeTab
            };
        case SET_BREAKDOWN:
            return {
                ...state,
                showBreakdown: action.payload.showBreakdown,
                breakdownActionSource: action.payload.breakdownActionSource
            };
        case SET_ACTIVE_BREAKDOWN_TAB_DETAILS:
            return {
                ...state,
                activeBreakdownTabDetails: {
                    ...state.activeBreakdownTabDetails,
                    ...action.payload.activeBreakdownTabDetails
                }
            };
        case SAVE_VEHICLE_INSURANCE_INFO: {
            return {
                ...state,
                vehicleInsuranceInfo: action.payload
            };
        }
        case SHOW_VEHICLE_INSURANCE:
            return {
                ...state,
                showVehicleInsurance: action.payload.showVehicleInsurance
            };
        case SHOW_CUSTOMER_RISK_AND_COVERAGE_SLIDEOUT:
            return {
                ...state,
                showCustomerRiskAndCoverageSlideOut: action.payload.showCustomerRiskAndCoverageSlideOut
            };
        case SET_SLIDEOUT_CONTENT:
            return {
                ...state,
                slideOutContent: action.payload.slideOutContent
            };
        case SHOW_CREDIT_BUREAU:
            return {
                ...state,
                showCreditBureau: action.payload.showCreditBureau
            };
        case INIT_PENCIL_SUCCESS: {
            return {
                ...state,
                vehicleInsuranceInfo: action.payload.vehicleInsurance,
                rightToCancelDetails: action.payload.rightToCancelDetails ?? state.rightToCancelDetails,
                showCustomerInsightsSlideout: false,
                showRepairOrdersSlideout: false
            };
        }

        case SHOW_BREAKDOWN_SLIDEOUT:
            return {
                ...state,
                showBreakdownSlideout: action.payload.showBreakdownSlideout
            };

        case SHOW_CUSTOMER_INSIGHTS_SLIDEOUT:
            return {
                ...state,
                showCustomerInsightsSlideout: action.payload.showCustomerInsightsSlideout
            };

        case SHOW_REPAIR_ORDERS_SLIDEOUT:
            return {
                ...state,
                showRepairOrdersSlideout: action.payload.showRepairOrdersSlideout,
                // Kept when closing, which carries no vin: the panel animates out and still needs its list
                repairOrdersSlideoutVin: action.payload.repairOrdersSlideoutVin ?? state.repairOrdersSlideoutVin
            };

        case INCENTIVES_SLIDEOUT_TAB_CHANGE: {
            return {
                ...state,
                incentivesSlideout: {
                    activeTab: action.payload.activeTab
                }
            };
        }
        case COMPLIANCE_SLIDEOUT_TAB_CHANGE: {
            return {
                ...state,
                customerRiskAndCoverageSlideout: {
                    activeTab: action.payload.activeTab
                }
            };
        }
        case TRADE_IN_SLIDEOUT_TAB_CHANGE: {
            return {
                ...state,
                tradeInSlideout: {
                    activeTab: action.payload.activeTab
                }
            };
        }
        case SET_FUNCTIONS_MODAL:
            return {
                ...state,
                showFunctionsModal: action.payload.showFunctionsModal
            };
        case SET_AUDIT_LOG_SLIDEOUT:
            return {
                ...state,
                showAuditLogSlideout: action.payload.showAuditLogSlideout
            };
        case SET_IS_PAYMENT_CALL_PENDING:
            return {
                ...state,
                paymentCallPending: action.payload.paymentCallPending
            };
        case SET_PAYMENT_IN_FLIGHT_COUNT:
            return {
                ...state,
                inFlightPaymentCount: action.payload.count
            };
        case SHOW_FAILED_UPDATE_OFFER_MODAL:
            return {
                ...state,
                showFailedUpdateOfferModal: true
            };
        case HIDE_FAILED_UPDATE_OFFER_MODAL:
            return {
                ...state,
                showFailedUpdateOfferModal: false
            };
        case SET_CALC_ENGINE_TRANSITION_STATE:
            return {
                ...state,
                calcEngineTransitionState: {
                    ...action.payload
                }
            };

        case SET_IS_AUTOSELECT_PROGRAM_CHECKED:
            return {
                ...state,
                isAutoSelectProgramChecked: action.payload
            };
        case UPDATE_RIGHT_TO_CANCEL_DAYS:
            return {
                ...state,
                rightToCancelDetails: {
                    ...state.rightToCancelDetails,
                    rightToCancelDays: action.payload.rightToCancelDays
                }
            };
        case UPDATE_RIGHT_TO_CANCEL_CHARGE:
            return {
                ...state,
                rightToCancelDetails: {
                    ...state.rightToCancelDetails,
                    rightToCancelCharge: action.payload.rightToCancelCharge
                }
            };
        case BALLOON_FALLBACK_HIGHLIGHT:
            return {
                ...state,
                hasBalloonFallbackOccurred: true
            };
        case BALLOON_FALLBACK_HIGHLIGHT_CLEAR:
            return {
                ...state,
                hasBalloonFallbackOccurred: false
            };
        case SOCKET_EVENT_RECEIVED: {
            return {
                ...state,
                vehicleInsuranceInfo: action.payload?.data?.vehicleInsurance,
                isVehicleInsuranceUpdated: action.payload?.isVehicleInsuranceUpdated
            };
        }
        case AIO_DXG_EVENTS: {
            const event = action.payload;
            const isFsPatchAndNameChangeErrorsEnabled = action.meta?.isFsPatchAndNameChangeErrorsEnabled || false;
            const isFsSyncFailureMessagingEnabled = action.meta?.isFsSyncFailureMessagingEnabled || false;

            const fsFailureEvent = event as AioDxgEvent | undefined;

            const fsSyncFailure: AioFsSyncFailure | undefined =
                isFsSyncFailureMessagingEnabled &&
                fsFailureEvent?.type === 'fsFailure' &&
                Array.isArray(fsFailureEvent.normalizedErrors)
                    ? {
                          normalizedErrors: fsFailureEvent.normalizedErrors,
                          coxAutoCorrelationId: event['X-CoxAuto-Correlation-Id'],
                          retryable: fsFailureEvent.retryable,
                          statusCode: fsFailureEvent.statusCode,
                          attempt: fsFailureEvent.attempt,
                          maxAttempts: fsFailureEvent.maxAttempts,
                          dealMethod: fsFailureEvent.dealMethod,
                          dealRefId: fsFailureEvent.dealRefId
                      }
                    : undefined;

            if (event?.type === 'fsFailure' && Array.isArray(event.data)) {
                const validEventReasons = ['CreateBadRequest'];

                if (isFsPatchAndNameChangeErrorsEnabled) {
                    validEventReasons.push('PatchBadRequest');
                }

                const shouldShowError = validEventReasons.includes(event.reason);

                if (shouldShowError) {
                    return {
                        ...state,
                        aioDxgEvents: {
                            ...state.aioDxgEvents,
                            fsFailures: event.data,
                            ...(fsSyncFailure && { fsSyncFailure })
                        }
                    };
                }
            }
            if (event?.type === 'fsSuccess' && !event.fsNoOp && event.data?.dealRefId) {
                return {
                    ...state,
                    aioDxgEvents: {
                        ...withoutFsSyncFailure(state.aioDxgEvents),
                        fsFailures: []
                    }
                };
            }
            if (fsSyncFailure) {
                return {
                    ...state,
                    aioDxgEvents: {
                        ...state.aioDxgEvents,
                        fsSyncFailure
                    }
                };
            }
            return state;
        }
        case CLEAR_AIO_DXG_EVENT_ERRORS: {
            return {
                ...state,
                aioDxgEvents: {
                    ...withoutFsSyncFailure(state.aioDxgEvents),
                    fsFailures: []
                }
            };
        }
        case DISMISS_FS_SYNC_FAILURE: {
            const aioDxgEvents = withoutFsSyncFailure(state.aioDxgEvents);

            return aioDxgEvents === state.aioDxgEvents ? state : { ...state, aioDxgEvents };
        }
        case FS_SYNC_FINAL_ATTEMPT_TIMED_OUT: {
            const fsSyncFailure = state.aioDxgEvents?.fsSyncFailure;

            if (!fsSyncFailure || (fsSyncFailure.attempt === undefined && fsSyncFailure.maxAttempts === undefined)) {
                return state;
            }

            const { attempt: _attempt, maxAttempts: _maxAttempts, ...remaining } = fsSyncFailure;

            return { ...state, aioDxgEvents: { ...state.aioDxgEvents, fsSyncFailure: remaining } };
        }
        case OPEN_SLIDEOUT:
            return {
                ...state,
                currentSlideOut: action.payload,
                previousSlideOut:
                    action.previousSlideOut && action.previousSlideOut !== 'none' ? action.previousSlideOut : undefined,
                appraisalTradeInIndex: action.appraisalTradeInIndex
            };
        case CLOSE_SLIDEOUT: {
            const restoredSlideOut = state.previousSlideOut || 'none';
            return {
                ...state,
                currentSlideOut: restoredSlideOut,
                previousSlideOut: undefined,
                appraisalTradeInIndex: undefined,
                ...(restoredSlideOut === 'none' ? { tradeInSlideout: { activeTab: 'trade-in' as TradeInSlideoutTab } } : {})
            };
        }
        case SHOW_UPDATE_OFFER_FOR_FS_EVENTS:
            return {
                ...state,
                showUpdateOfferForFSEvents: action.payload.showUpdateOfferForFSEvents
            };
        case SET_PENDING_NAVIGATION:
            return {
                ...state,
                pendingNavigation: action.payload.url || null
            };
        case CLEAR_PENDING_NAVIGATION:
            return {
                ...state,
                pendingNavigation: null
            };

        default:
            return state;
    }
};
